import { DtoV1PropertyTaxEscrowInstallment } from '~/__generated__/api';
import Stack from '~/common/components/Stacks/Stack';
import Text from '~/common/components/Texts/Text';
import { Chip } from '~/common/mui/Chip';
import { formatCurrency } from '~/common/util/format';

import { formatTaxDate } from '../helpers';
import TaxSummaryItem from './TaxSummaryItem';

type CollectAtClosingProps = {
  allInstallmentsCollectedAtClosing: (DtoV1PropertyTaxEscrowInstallment & {
    isDueBetweenClosingAndFirstPaymentDate: boolean;
    isSupplemental?: boolean;
  })[];
  delinquentTaxesTotal: number;
  allInstallmentsCollectedAtClosingTotal: number;
  supplementalTaxesPaidAtClosing?: number;
};

export const CollectAtClosing = ({
  allInstallmentsCollectedAtClosing,
  delinquentTaxesTotal,
  allInstallmentsCollectedAtClosingTotal,
  supplementalTaxesPaidAtClosing = 0,
}: Readonly<CollectAtClosingProps>) => {
  const getInstallmentLabel = (
    installment: CollectAtClosingProps['allInstallmentsCollectedAtClosing'][number],
  ) => {
    if (installment.isSupplemental) {
      return `${formatTaxDate(installment.distributionDate)} Supplemental`;
    }
    if (installment.isDueBetweenClosingAndFirstPaymentDate) {
      return `${formatTaxDate(installment.distributionDate)} - Due between closing and first payment date`;
    }
    return formatTaxDate(installment.distributionDate);
  };

  return (
    <Stack sx={{ pt: 8 }}>
      <Stack sx={{ pl: '2rem', gap: 6 }}>
        <Stack sx={{ alignItems: 'flex-start' }}>
          <Text
            sx={{
              fontWeight: '600',
            }}
          >
            Taxes Collected at Closing
          </Text>
        </Stack>
        <Stack gap={3} sx={{ p: '0 2rem 0 2rem' }}>
          {allInstallmentsCollectedAtClosing.map((installment) => (
            <TaxSummaryItem
              key={installment.installmentId}
              label={getInstallmentLabel(installment)}
              content={formatCurrency(installment.installmentAmount)}
            />
          ))}
          {delinquentTaxesTotal > 0 && (
            <TaxSummaryItem
              label={'Delinquent Taxes'}
              content={formatCurrency(delinquentTaxesTotal)}
              data-testid="delinquent-taxes"
            />
          )}
          {supplementalTaxesPaidAtClosing > 0 && (
            <TaxSummaryItem
              label={'Supplemental Taxes'}
              content={formatCurrency(supplementalTaxesPaidAtClosing)}
              data-testid="supplemental-taxes-paid-at-closing"
            />
          )}
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '0.5rem',
            }}
          >
            <Text
              sx={{
                fontSize: '1rem',
                fontWeight: '600',
              }}
            >
              Total:
            </Text>
            <Chip
              label={`= ${formatCurrency(allInstallmentsCollectedAtClosingTotal + delinquentTaxesTotal)}`}
              sx={{
                borderRadius: '0.5rem',
                fontWeight: '600',
              }}
              data-testid="taxes-collected-at-closing-total"
              color="success"
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
